import { type Legend, type Catalog } from '@/api/queryCatalog';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import styles from './index.module.less';

export type SelectData = Catalog &
  Legend & {
    partNo?: string;
  };

export type CatalogProps = {
  onSelect: (data: SelectData) => void;
  data: Catalog[];
  modelCode?: string;
  legendCode?: string;
  partNo?: string;
  select?: SelectData;
};

const { DirectoryTree } = Tree;

function getSelectData(data: Catalog[], selectdKeys: string[]) {
  if (selectdKeys.length < 1) return;
  const selectKey = selectdKeys[0];
  let selectData: SelectData | undefined;
  data.find(group => {
    if (group.groupCode === selectKey) {
      selectData = group;
      return true;
    }
    return group.legendList?.find(legend => {
      if (legend.legendCode === selectKey) {
        selectData = legend;
        return true;
      }
      return false;
    });
  });
  return selectData;
}

function getGroupCode(data: Catalog[], selectdKeys: string[]) {
  if (selectdKeys.length < 1) return;
  const selectKey = selectdKeys[0];
  const catalog = data.find(group => {
    return group.legendList?.find(legend => {
      if (legend.legendCode === selectKey) {
        return true;
      }
      return false;
    });
  });
  return catalog?.groupCode;
}

const CatalogTree = (props: CatalogProps) => {
  // 初始化树形数据、默认展开的节点、默认选中的节点
  const [{ treeData, defaultExpandedKeys, defaultSelectedKeys }] = useState(() => {
    const expandedKeys: string[] = [];
    const selectedKeys: string[] = [];
    const tree: DataNode[] = props.data.map(group => {
      return {
        title: group.groupName!,
        key: group.groupCode!,
        children: group.legendList?.map(legend => {
          if (props.legendCode === legend.legendCode) {
            expandedKeys.push(group.groupCode!);
            selectedKeys.push(legend.legendCode!);
          }
          return {
            title: legend.legendName!,
            key: legend.legendCode!
          };
        })
      };
    });
    if (expandedKeys.length === 0) {
      const selectGroup = props.data.find(group => group.legendList?.length);
      if (selectGroup) {
        expandedKeys.push(selectGroup.groupCode!);
        selectedKeys.push(selectGroup.groupCode!);
      } else if (props.data.length) {
        selectedKeys.push(props.data[0].groupCode!);
      }
    }
    return {
      treeData: tree,
      defaultExpandedKeys: expandedKeys,
      defaultSelectedKeys: selectedKeys
    };
  });
  // 控制展开的节点
  const [expandedKeys, setExpandedKeys] = useState<any[]>(defaultExpandedKeys);
  useEffect(() => {
    const key = getGroupCode(
      props.data,
      props.select?.legendCode ? [props.select?.legendCode] : []
    );
    if (key) {
      setExpandedKeys(state => {
        if (!state.includes(key)) return [...state, key];
        return state;
      });
    }
  }, [props.select, props.data]);
  // 控制选中的节点
  const oldDefaultSelectedKeys = useRef<string[]>();
  useEffect(() => {
    if (oldDefaultSelectedKeys.current === defaultSelectedKeys) return;
    oldDefaultSelectedKeys.current = defaultSelectedKeys;

    const selectData = getSelectData(props.data, defaultSelectedKeys);
    if (selectData) {
      props.onSelect({ ...selectData, partNo: props.partNo as string });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedKeys]);
  const selectedKeys = useMemo(() => {
    if (!props.select) return [];
    if (props.select.legendCode) {
      return [props.select.legendCode];
    }
    if (props.select.groupCode) {
      return [props.select.groupCode];
    }
    return [];
  }, [props.select]);
  const onSelect = useCallback(
    sks => {
      if (sks.length < 1) return;
      const selectData = getSelectData(props.data, sks);
      if (selectData) {
        props.onSelect({ ...selectData });
      }
    },
    [props]
  );

  return (
    <DirectoryTree
      treeData={treeData}
      onSelect={onSelect}
      defaultSelectedKeys={defaultSelectedKeys}
      blockNode={true}
      className={styles['catalog-tree']}
      defaultCheckedKeys={defaultSelectedKeys}
      selectedKeys={selectedKeys}
      onExpand={setExpandedKeys}
      expandedKeys={expandedKeys}
    />
  );
};

export default CatalogTree;
